import {EventEmitter} from "eventemitter3";

import {PlayerReplay, ReplayEntityV2} from "../../typesV2";
import {encodeActionsToRLE} from "../../utils/utils";
import {Player} from "../player/Player.js";
import {
  DesyncPayload,
  PlayerController,
} from "../player/controller/PlayerController.js";
import {
  SimulationEvent,
  SimulationEventType,
  SimulationManager,
  SimulationSnapshot,
} from "./SimulationManager.js";
import {World} from "./World.js";

/**
 * Defines the events that a GameServer instance can emit.
 */
export type GameServerEvents = {
  /** Fired once when the simulation for this server is started or reset. */
  "round-started": (server: GameServer) => void;
  /** Fired for every simulation event generated by the SimulationManager. */
  "simulation-event": (event: SimulationEvent) => void;
  /** Fired once when the game round concludes. */
  "round-finished": (result: {completed: boolean; dead: boolean}) => void;
};

type StateHistoryEntry = {
  step: number;
  snapshot: SimulationSnapshot;
};

/**
 * A reusable, stateful server that runs a complete, independent game simulation.
 * It extends EventEmitter to broadcast its state changes to the GameManager.
 */
export class GameServer extends EventEmitter<GameServerEvents> {
  public readonly id: string;
  public readonly simulation: SimulationManager;
  public readonly controllers = new Map<string, PlayerController>();
  public isRoundOver = false;

  /**
   * The interpolation factor (0.0 to 1.0) between the previous and current
   * simulation step used for smooth visual rendering.
   */
  public subStepAlpha = 0;

  private startTime = 0;
  static readonly STEPS_PER_SECOND = 40;
  private readonly timePerStep: number = 1 / GameServer.STEPS_PER_SECOND; // 40 steps per second
  private readonly KEYFRAME_INTERVAL = 8; // Save a snapshot every 8 steps (~0.2s)
  private readonly HISTORY_DURATION_SECONDS = 15;
  private readonly HISTORY_CAPACITY = Math.ceil(
    (GameServer.STEPS_PER_SECOND / this.KEYFRAME_INTERVAL) *
      this.HISTORY_DURATION_SECONDS,
  );

  // --- State for History Ring Buffer and Resimulation ---
  private stateHistory: StateHistoryEntry[] = [];
  private stateHistoryIndex = 0;
  private resimulatingUntilStep: number | null = null;

  constructor(
    id: string,
    world: World,
    players: Player[],
    controllers: PlayerController[],
  ) {
    super();
    this.id = id;
    this.simulation = new SimulationManager(world, players);
    for (const controller of controllers) {
      this.controllers.set(controller.uid, controller);
      controller.on("desync-detected", this.handleDesync);
    }
  }

  /**
   * Starts or restarts the simulation for this server.
   */
  public start(startTime: number): void {
    console.log(
      `[GameServer ${this.id}] Starting simulation at ${new Date(startTime).toLocaleTimeString()}`,
    );
    this.startTime = startTime;
    this.isRoundOver = false;
    this.subStepAlpha = 0;
    this.simulation.reset();
    for (const controller of this.controllers.values()) {
      controller.reset();
    }
    // Reset history and resimulation state
    this.stateHistory = new Array(this.HISTORY_CAPACITY);
    this.stateHistoryIndex = 0;
    this.resimulatingUntilStep = null;

    this.stateHistory[0] = {
      step: 0,
      snapshot: this.simulation.getStateSnapshot(),
    };
    this.stateHistoryIndex = 1;

    this.emit("round-started", this);
  }

  /**
   * The main update loop, called by the GameManager every visual frame.
   * @param serverNow The current, synchronized server time.
   */
  public update(serverNow: number): void {
    // --- 1. Calculate current time and interpolation alpha ---
    if (serverNow < this.startTime) {
      this.subStepAlpha = 0;
      return;
    }

    const elapsedTime = (serverNow - this.startTime) / 1000;
    const currentFrameTime = elapsedTime / this.timePerStep;

    // The alpha is the fractional part of where we are between simulation steps.
    // We subtract the *current* simulation step, which is the last one completed.
    this.subStepAlpha = currentFrameTime % 1;

    // If the round is over, there's no more simulation to run.
    if (this.isRoundOver) return;
    const targetStep = Math.floor(currentFrameTime);
    this.runSimulationUntil(targetStep);
  }

  /**
   * The core simulation loop, now extracted into its own method.
   * Runs the simulation from its current step up to a target step.
   * @param targetStep The simulation step to advance to.
   */
  private runSimulationUntil(targetStep: number): void {
    let stepsDone = 0;
    while (this.simulation.currentStep < targetStep && stepsDone++ < 16) {
      if (this.isRoundOver) break;

      // 1. Get actions from all controllers
      const actionsForStep: Record<string, boolean> = {};
      for (const [playerId, controller] of this.controllers.entries()) {
        actionsForStep[playerId] = controller.getActionForStep(
          this.simulation.currentStep,
        );
      }

      // 2. Run one simulation step
      const events = this.simulation.runStep(actionsForStep);

      // --- Event Emission Control for Resimulation ---
      let shouldEmitEvents = true;
      if (this.resimulatingUntilStep !== null) {
        if (this.simulation.currentStep < this.resimulatingUntilStep) {
          shouldEmitEvents = false;
        } else {
          this.resimulatingUntilStep = null;
        }
      }

      for (const event of events) {
        if (
          shouldEmitEvents ||
          event.type in
            [
              SimulationEventType.PlayerHitHazard,
              SimulationEventType.PlayerOutOfBounds,
              SimulationEventType.PlayerHitShard,
            ]
        ) {
          this.emit("simulation-event", event);
        }
      }

      if (this.simulation.isWon) {
        this.isRoundOver = true;
        this.emit("round-finished", {completed: true, dead: false});
        break;
      } else if (this.simulation.isLost) {
        this.isRoundOver = true;
        this.emit("round-finished", {completed: false, dead: true});
        break;
      }

      // Save a keyframe snapshot to the ring buffer if we are on the interval.
      if (this.simulation.currentStep % this.KEYFRAME_INTERVAL === 0) {
        const entry = {
          step: this.simulation.currentStep,
          snapshot: this.simulation.getStateSnapshot(),
        };
        this.stateHistory[this.stateHistoryIndex] = entry;
        this.stateHistoryIndex =
          (this.stateHistoryIndex + 1) % this.HISTORY_CAPACITY;
      }
    }
  }

  private handleDesync = (payload: DesyncPayload) => {
    const {rollbackToStep} = payload;
    if (rollbackToStep >= this.simulation.currentStep) return;

    const keyframeStep =
      rollbackToStep - (rollbackToStep % this.KEYFRAME_INTERVAL);

    // Search the ring buffer backwards for the desired keyframe.
    let foundEntry: StateHistoryEntry | undefined;
    for (let i = 0; i < this.HISTORY_CAPACITY; i++) {
      const index =
        (this.stateHistoryIndex - 1 - i + this.HISTORY_CAPACITY) %
        this.HISTORY_CAPACITY;
      const entry = this.stateHistory[index];
      if (entry && entry.step === keyframeStep) {
        foundEntry = entry;
        break;
      }
      // Optimization: if we find a step older than what we need, we can stop searching.
      if (entry && entry.step < keyframeStep) break;
    }

    if (!foundEntry) {
      console.error(
        `[ROLLBACK] Cannot rollback to step ${rollbackToStep}. No keyframe found at or before (expected at ${keyframeStep}). This is a critical error.`,
      );
    }

    // Set the high-water mark for resimulation. Events will be suppressed until we pass this step.
    this.resimulatingUntilStep = rollbackToStep;
    this.isRoundOver = false; // A rollback might un-finish the round.

    // Load the state. The next call to update() will automatically begin
    // re-simulating from the restored step to catch up.
    if (foundEntry) this.simulation.loadStateSnapshot(foundEntry.snapshot);
    else this.simulation.reset();
  };

  public destroy(): void {
    for (const controller of this.controllers.values()) {
      controller.off("desync-detected", this.handleDesync);
    }
    this.removeAllListeners();
  }

  public createReplay(levelId: string): ReplayEntityV2 {
    const playerReplays: Record<string, PlayerReplay> = {};
    for (const [playerId, player] of this.simulation.players) {
      const controller = this.controllers.get(playerId)!;
      playerReplays[controller.uid] = {
        id: controller.uid,
        ownerId: player.ownerId,
        instanceId: player.instanceId,
        actions: encodeActionsToRLE(controller.getAllActions()),
        finalState: {
          position: player.currentPosition,
          died: player.isDead,
          collectedShards: player.collectedShardsWithSteps,
          velocity: player.velocity,
        },
      } as PlayerReplay;
    }
    const totalSteps = this.simulation.isWon
      ? this.simulation.getLastShardCollectedStep()
      : this.simulation.currentStep;
    return {
      completed: this.simulation.isWon,
      levelId,
      playerReplays,
      schemaVersion: 2,
      totalSteps,
    } as const;
  }
}
